import React, { useState, useEffect, useCallback } from "react";

//MUI components
import { CircularProgress, IconButton, Typography, InputAdornment, TextField, Tooltip, Menu, MenuItem } from "@material-ui/core";
import { DataGrid } from "@mui/x-data-grid";

//Icons
import RefreshIcon from "@material-ui/icons/Refresh";
import SearchIcon from "@material-ui/icons/Search";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import AddIcon from "@material-ui/icons/Add";
import PublishIcon from "@material-ui/icons/Publish";

//Custom portal components
import CustomButton from "../common/CustomButton";
import ContactEditSection from "./ContactEditSection";
import ImportContactsModal from "./Modals/ImportContactsModal";
import {
    getWhatsAppContacts,
    createWhatsAppContact,
    updateWhatsAppContact,
    deleteWhatsAppContacts,
    importWhatsAppContacts,
} from "../DataFunctions";

//External libraries
import Swal from "sweetalert2";

//styleSheets
import "./Styles/contactsSection.css";

const localizedTextsMap = {
    noRowsLabel: "No hay contactos para mostrar",
    columnHeaderSortIconLabel: "Ordenar columna",
    footerRowSelected: (count) => `${count.toLocaleString()} contacto(s) seleccionado(s)`,
};

/**
 * Interpreta la respuesta del API de contactos.
 * El endpoint responde HTTP 200 aun cuando hay contactos inválidos o
 * duplicados; el resultado real viene en el body: { response: { code, message, summary } }.
 *
 * Devuelve: { ok, inserted, duplicated, invalid, message, detailHtml }
 * - ok: true solo si se insertó al menos un contacto y no hubo inválidos.
 */
const interpretContactResponse = (res) => {
    // Error de red / transporte.
    if (!res || res.error || res instanceof Error || res?.response?.status >= 400) {
        return {
            ok: false,
            transportError: true,
            message: "No se pudo conectar con el servicio. Intenta nuevamente más tarde.",
        };
    }

    const response = res?.data?.response ?? res?.data ?? {};
    const summary = response.summary ?? {};
    const inserted = summary.inserted ?? summary.agregados ?? 0;
    const duplicated = Array.isArray(summary.duplicated) ? summary.duplicated : [];
    const invalid = Array.isArray(summary.invalid) ? summary.invalid : [];

    // Construir detalle HTML con los errores de cada contacto inválido.
    let detailHtml = "";
    if (invalid.length > 0) {
        detailHtml = invalid
            .map((item) => {
                const phone = item.phone_number ?? item.phoneNumber ?? "";
                const errs = Array.isArray(item.errors) ? item.errors : [item.errors].filter(Boolean);
                const errList = errs.map((e) => `<li style="text-align:left">${e}</li>`).join("");
                return `<div style="margin-bottom:8px"><strong>${phone || "Contacto"}:</strong><ul style="margin:4px 0 0">${errList}</ul></div>`;
            })
            .join("");
    }

    // Si hay summary, el éxito depende de que se haya insertado algo sin inválidos.
    const hasSummary = summary && Object.keys(summary).length > 0;
    const ok = hasSummary ? inserted > 0 && invalid.length === 0 : true;

    return {
        ok,
        inserted,
        duplicated,
        invalid,
        message: response.message || "",
        detailHtml,
    };
};

// Capitaliza la primera letra de un texto (como en segmentación).
const capitalize = (text) => {
    const str = (text ?? "").toString().trim();
    if (!str) return "";
    return str.charAt(0).toUpperCase() + str.slice(1);
};

// Etiquetas de género en español a partir del valor del backend.
// El backend puede enviar "F"/"M", "Female"/"Male" o "Femenino"/"Masculino".
const genderLabel = (rawGender) => {
    const g = (rawGender ?? "").toString().trim().toLowerCase();
    switch (g) {
        case "f":
        case "female":
        case "femenino":
            return "Femenino";
        case "m":
        case "male":
        case "masculino":
            return "Masculino";
        default:
            return "No especificado";
    }
};

// Normaliza un contacto del endpoint a las columnas de la tabla.
// Modelo real del endpoint:
//   { phone_number, name, age (puede ser null), gender ("Unknown" | ...), ... }
// Nota: Origen, Estado y Etiquetas son campos nuevos cuyo modelo aún se está
// definiendo; se mapean de forma tolerante y muestran "—" cuando no existen.
const normalizeContact = (contact, index) => {
    const name = contact.name ?? "";
    const phone = contact.phone_number ?? "";
    const genderValue = contact.gender ?? "Unknown";
    const age = contact.age === null || contact.age === undefined ? "" : contact.age;
    // Campos del modelo real: origin (ej. "WhatsApp") y status (ej. "Nuevo").
    const origin = contact.origin ?? "";
    const status = contact.status ?? "";
    const rawTags = contact.tags ?? contact.labels ?? contact.etiquetas ?? [];
    const tags = Array.isArray(rawTags) ? rawTags : rawTags ? [rawTags] : [];
    // Campos adicionales editables (opcionales).
    const email = contact.email ?? "";
    const state = contact.state ?? "";
    const locality = contact.locality ?? "";
    // intent / interests pueden venir como arreglo, string separado por comas, o faltar.
    const toArray = (value) => {
        if (Array.isArray(value)) return value;
        if (typeof value === "string" && value.trim()) {
            return value.split(",").map((v) => v.trim()).filter(Boolean);
        }
        return [];
    };
    const intent = toArray(contact.intent ?? contact.intents ?? contact.intention);
    // Intereses: mismo campo que usa la segmentación (contact.interests).
    // Se contemplan variantes por robustez.
    const interests = toArray(
        contact.interests ?? contact.interest ?? contact.intereses ?? contact.tags ?? contact.labels
    );
    // Campos de solo lectura (detalle).
    const sentiment = contact.sentiment ?? "";
    const interactionsCounter =
        contact.interactionsCounter ?? contact.interactions ?? contact.interaccionesCounter ?? null;
    // update_date puede venir como { $date: <ms> }, número (ms) o string ISO.
    const rawUpdate = contact.update_date ?? contact.updateDate ?? contact.lastModified ?? null;
    const updateDate =
        rawUpdate && typeof rawUpdate === "object"
            ? rawUpdate.$date ?? rawUpdate.date ?? null
            : rawUpdate;
    return {
        // El teléfono es el identificador natural del contacto.
        id: phone || contact.id || contact._id || index,
        name,
        phone,
        genderValue,
        gender: genderLabel(genderValue),
        age,
        origin,
        status,
        tags,
        email,
        state,
        locality,
        intent,
        interests,
        sentiment,
        interactionsCounter,
        updateDate,
    };
};

/**
 * Sección de Contactos para la página de WhatsApp.
 * - Lista los contactos del endpoint REST con token de acceso.
 * - Selección múltiple con un único botón Eliminar (deshabilitado sin selección).
 * - Editar/crear se hace en una sub-sección (como el detalle de campañas).
 * - Importar archivo de contactos.
 */
const ContactsSection = (props) => {
    const { active } = props;
    const [contacts, setContacts] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [errorInFetch, setErrorInFetch] = useState(false);
    const [query, setQuery] = useState("");
    const [hasLoaded, setHasLoaded] = useState(false);
    const [selectionModel, setSelectionModel] = useState([]);
    const [pageSize, setPageSize] = useState(10);

    // Filtros por columna (tipo select). "" = sin filtro (todos).
    const [columnFilters, setColumnFilters] = useState({
        gender: "",
        state: "",
        locality: "",
        status: "",
    });

    // Control del menú desplegable de filtro: qué columna está abierta y su anchor.
    const [filterMenu, setFilterMenu] = useState({ field: null, anchorEl: null });

    const openFilterMenu = (field) => (event) => {
        event.stopPropagation();
        setFilterMenu({ field, anchorEl: event.currentTarget });
    };

    const closeFilterMenu = () => setFilterMenu({ field: null, anchorEl: null });

    const selectColumnFilter = (field, value) => {
        setColumnFilters((prev) => ({ ...prev, [field]: value }));
        closeFilterMenu();
    };

    // Navegación interna: "list" | "edit"
    const [view, setView] = useState("list");
    const [editState, setEditState] = useState({ mode: "create", contact: null });

    // Modal de importación
    const [importOpen, setImportOpen] = useState(false);

    const fetchContacts = useCallback(async () => {
        setLoading(true);
        setErrorInFetch(false);
        setSelectionModel([]);
        const res = await getWhatsAppContacts();

        if (res?.error || res?.response?.status >= 400 || res instanceof Error) {
            setContacts([]);
            setTotal(0);
            setErrorInFetch(true);
            setLoading(false);
            setHasLoaded(true);
            return;
        }

        // El endpoint devuelve: { response: { message, total, contacts: [...] } }
        const rawData = res?.data;
        const list = Array.isArray(rawData)
            ? rawData
            : rawData?.response?.contacts ||
              rawData?.contacts ||
              rawData?.data ||
              rawData?.contactList ||
              [];
        const totalCount =
            rawData?.response?.total ?? rawData?.total ?? (Array.isArray(list) ? list.length : 0);

        // Log temporal para diagnosticar los campos que llegan (ej. interests/intereses).
        if (list.length > 0) {
            console.log("ContactsSection — primer contacto crudo:", JSON.stringify(list[0]));
        }

        const normalized = list.map((contact, index) => normalizeContact(contact, index));
        if (normalized.length > 0) {
            console.log("ContactsSection — primer contacto normalizado:", normalized[0]);
        }
        setContacts(normalized);
        setTotal(totalCount);
        setLoading(false);
        setHasLoaded(true);
    }, []);

    // Cargar la primera vez que la sección se vuelve activa.
    useEffect(() => {
        if (active && !hasLoaded) {
            fetchContacts();
        }
    }, [active, hasLoaded, fetchContacts]);

    // Opciones únicas por columna para poblar los selects de filtro.
    // Se calculan a partir de los contactos cargados, ignorando vacíos.
    const uniqueOptions = (field) => {
        const set = new Set();
        contacts.forEach((contact) => {
            const value = (contact[field] ?? "").toString().trim();
            if (value) set.add(value);
        });
        return Array.from(set).sort((a, b) => a.localeCompare(b, "es"));
    };

    // Opciones por campo para poblar el menú de filtro según la columna abierta.
    const filterOptionsByField = {
        gender: uniqueOptions("gender"),
        state: uniqueOptions("state"),
        locality: uniqueOptions("locality"),
        status: uniqueOptions("status"),
    };

    const filteredContacts = contacts.filter((contact) => {
        const search = query.toLowerCase();
        const name = (contact.name || "").toString().toLowerCase();
        const phone = (contact.phone || "").toString().toLowerCase();
        const matchesSearch = name.includes(search) || phone.includes(search);

        // Filtros por columna: si el filtro está vacío, no restringe.
        const matchesColumn = Object.keys(columnFilters).every((field) => {
            const selected = columnFilters[field];
            if (!selected) return true;
            return (contact[field] ?? "").toString() === selected;
        });

        return matchesSearch && matchesColumn;
    });

    // ---- Navegación / acciones ----

    const goToCreate = () => {
        setEditState({ mode: "create", contact: null });
        setView("edit");
    };

    const goToEdit = (contact) => {
        setEditState({ mode: "edit", contact });
        setView("edit");
    };

    const backToList = () => setView("list");

    const handleSaveContact = async (payload, mode) => {
        const res =
            mode === "edit"
                ? await updateWhatsAppContact(payload)
                : await createWhatsAppContact(payload);

        const result = interpretContactResponse(res);

        if (!result.ok) {
            Swal.fire({
                icon: "error",
                title: mode === "edit" ? "No se pudo actualizar el contacto" : "No se pudo agregar el contacto",
                html:
                    result.detailHtml ||
                    (result.message ? `<p>${result.message}</p>` : "Intenta nuevamente más tarde."),
            });
            return false;
        }

        await fetchContacts();
        Swal.fire({
            icon: "success",
            title: mode === "edit" ? "Contacto actualizado" : "Contacto agregado",
            text: result.message || undefined,
            timer: 1800,
            showConfirmButton: false,
        });
        return true;
    };

    const handleImport = async (contactsArray) => {
        const res = await importWhatsAppContacts(contactsArray);
        const result = interpretContactResponse(res);

        // Error de transporte: no se pudo ni procesar el archivo.
        if (result.transportError) {
            Swal.fire({
                icon: "error",
                title: "No se pudo importar el archivo",
                text: result.message,
            });
            return false;
        }

        const invalidCount = result.invalid.length;
        const duplicatedCount = result.duplicated.length;

        // Nada se insertó → error con el detalle de los inválidos.
        if (result.inserted === 0) {
            Swal.fire({
                icon: "error",
                title: "No se importó ningún contacto",
                html:
                    (result.message ? `<p>${result.message}</p>` : "") +
                    (result.detailHtml || ""),
            });
            // Igual refrescamos por si hubo cambios parciales.
            await fetchContacts();
            return false;
        }

        await fetchContacts();

        // Se insertó algo pero hubo inválidos/duplicados → aviso parcial.
        if (invalidCount > 0 || duplicatedCount > 0) {
            Swal.fire({
                icon: "warning",
                title: "Importación parcial",
                html:
                    (result.message ? `<p>${result.message}</p>` : "") +
                    (result.detailHtml || ""),
            });
            return true;
        }

        // Todo bien.
        Swal.fire({
            icon: "success",
            title: "Contactos importados",
            text: result.message || undefined,
            timer: 1800,
            showConfirmButton: false,
        });
        return true;
    };

    const handleDeleteSelected = async () => {
        if (selectionModel.length === 0) return;

        const confirm = await Swal.fire({
            title: `¿Eliminar ${selectionModel.length} contacto(s)?`,
            text: "Esta acción no se puede deshacer.",
            icon: "warning",
            showDenyButton: true,
            confirmButtonText: "Sí, eliminar",
            denyButtonText: "Cancelar",
            confirmButtonColor: "#E42922",
            denyButtonColor: "#27315d",
        });
        if (confirm.isDenied || confirm.isDismissed) return;

        // El id de cada fila es el phone_number.
        const phoneNumbers = selectionModel.map((id) => id.toString());
        const res = await deleteWhatsAppContacts(phoneNumbers);

        // Error de red/transporte.
        if (!res || res.error || res instanceof Error || res?.response?.status >= 400) {
            Swal.fire({
                icon: "error",
                title: "No se pudieron eliminar los contactos",
                text: "Intenta nuevamente más tarde.",
            });
            return;
        }

        // El API puede indicar un error de negocio en el body (type ERROR / FAIL).
        const response = res?.data?.response ?? res?.data ?? {};
        const type = (response.type || "").toString().toUpperCase();
        const businessError = type === "ERROR" || type === "FAIL" || type === "FAILURE";
        if (businessError) {
            Swal.fire({
                icon: "error",
                title: "No se pudieron eliminar los contactos",
                text: response.message || "Intenta nuevamente más tarde.",
            });
            return;
        }

        await fetchContacts();
        Swal.fire({
            icon: "success",
            title: "Contacto(s) eliminado(s)",
            text: response.message || undefined,
            timer: 1800,
            showConfirmButton: false,
        });
    };

    // Header de columna con un indicador de filtro desplegable (chevron ▾),
    // igual que el menú "Documentación" del menú principal. Al hacer clic se
    // abre un menú con las opciones únicas de la columna. Si hay un valor
    // seleccionado, el título lo muestra para saber que el filtro está activo.
    const renderFilterHeader = (title, field) => {
        const active = columnFilters[field];
        return (
            <div
                className="contacts-filter-header"
                onClick={openFilterMenu(field)}
            >
                <span style={{ fontWeight: 600 }}>{title}</span>
                <ExpandMoreIcon
                    fontSize="small"
                    className={active ? "contacts-filter-chevron-active" : ""}
                    style={{ marginLeft: 2 }}
                />
            </div>
        );
    };

    const columns = [
        {
            field: "contact",
            headerName: "Nombre",
            flex: 1.1,
            minWidth: 170,
            sortable: false,
            renderCell: (params) => (
                <div style={{ display: "flex", alignItems: "center", gap: 4, minWidth: 0 }}>
                    <Tooltip title="Modificar contacto">
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                goToEdit(params.row);
                            }}
                        >
                            <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <span className="contacts-name-link" onClick={() => goToEdit(params.row)}>
                        {params.row.name}
                    </span>
                </div>
            ),
        },
        {
            field: "phone",
            headerName: "Teléfono",
            flex: 1,
            minWidth: 140,
            renderCell: (params) => <span>{params.row.phone ? `+${params.row.phone}` : "—"}</span>,
        },
        {
            field: "email",
            headerName: "Email",
            flex: 1.4,
            renderCell: (params) => <span>{params.row.email || "—"}</span>,
        },
        {
            field: "gender",
            headerName: "Género",
            flex: 1,
            minWidth: 130,
            sortable: false,
            renderHeader: () => renderFilterHeader("Género", "gender"),
            renderCell: (params) => <span>{params.row.gender || "—"}</span>,
        },
        {
            field: "age",
            headerName: "Edad",
            width: 90,
            minWidth: 90,
            renderCell: (params) => <span>{params.row.age === "" ? "—" : params.row.age}</span>,
        },
        {
            field: "state",
            headerName: "Entidad Federativa",
            flex: 1.2,
            minWidth: 160,
            sortable: false,
            renderHeader: () => renderFilterHeader("Entidad Federativa", "state"),
            renderCell: (params) => <span>{params.row.state || "—"}</span>,
        },
        {
            field: "locality",
            headerName: "Localidad",
            flex: 1.2,
            minWidth: 150,
            sortable: false,
            renderHeader: () => renderFilterHeader("Localidad", "locality"),
            renderCell: (params) => <span>{params.row.locality || "—"}</span>,
        },
        {
            field: "interests",
            headerName: "Intereses",
            flex: 1.6,
            sortable: false,
            renderCell: (params) =>
                params.row.interests && params.row.interests.length > 0 ? (
                    <div className="contacts-interests-list">
                        {params.row.interests.map((interest, i) => (
                            <span key={i} className="contacts-interest-item">
                                {capitalize(interest)}
                            </span>
                        ))}
                    </div>
                ) : (
                    <span>—</span>
                ),
        },
        {
            field: "status",
            headerName: "Registro",
            flex: 1,
            minWidth: 130,
            sortable: false,
            renderHeader: () => renderFilterHeader("Registro", "status"),
            renderCell: (params) => <span>{params.row.status || "—"}</span>,
        },
    ];

    // ---- Vista de listado (el modal de edición se abre encima) ----
    return (
        <div className="contacts-section">
            {/* Búsqueda */}
            <div className="contacts-search-row">
                <Typography variant="body2" style={{ color: "#6b6b6b", marginBottom: 6 }}>
                    Buscar por nombre o teléfono
                </Typography>
                <TextField
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Ej. María López o 521234567890"
                    fullWidth
                    size="small"
                    variant="outlined"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon style={{ color: "#9a9a9a" }} />
                            </InputAdornment>
                        ),
                    }}
                />
            </div>

            {/* Botones de acción */}
            <div className="contacts-actions-row">
                <CustomButton
                    className="contacts-delete-btn"
                    startIcon={<DeleteOutlineIcon />}
                    onClick={handleDeleteSelected}
                    disabled={selectionModel.length === 0}
                >
                    Eliminar
                </CustomButton>
                <div className="contacts-actions-right">
                    <CustomButton
                        className="contacts-import-btn"
                        startIcon={<PublishIcon />}
                        onClick={() => setImportOpen(true)}
                    >
                        Importar
                    </CustomButton>
                    <CustomButton startIcon={<AddIcon />} onClick={goToCreate}>
                        Nuevo contacto
                    </CustomButton>
                    <IconButton title="Actualizar" onClick={fetchContacts}>
                        <RefreshIcon color="primary" />
                    </IconButton>
                </div>
            </div>

            {/* Listado */}
            <div className="contacts-list-card">
                <Typography variant="subtitle1" style={{ color: "#27315D", fontWeight: 600, marginBottom: 8 }}>
                    Listado de contactos
                </Typography>

                {loading ? (
                    <div className="center-loading-icon">
                        <CircularProgress />
                    </div>
                ) : errorInFetch ? (
                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                        <span>Hubo un error al cargar los contactos, intenta más tarde.</span>
                        <IconButton onClick={() => fetchContacts()}>
                            <RefreshIcon color="primary" width="20px" height="20px" />
                        </IconButton>
                    </div>
                ) : (
                    <div className="contacts-datagrid">
                        <DataGrid
                            disableColumnMenu
                            disableSelectionOnClick
                            checkboxSelection
                            rows={filteredContacts}
                            columns={columns}
                            localeText={localizedTextsMap}
                            autoHeight
                            rowHeight={72}
                            pageSize={pageSize}
                            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                            rowsPerPageOptions={[10, 25, 50]}
                            selectionModel={selectionModel}
                            onSelectionModelChange={(newSelection) => setSelectionModel(newSelection)}
                        />
                    </div>
                )}

                {/* Total de contactos al final de la tabla */}
                {!loading && !errorInFetch && (
                    <div className="contacts-total-footer">
                        <Typography variant="body2" style={{ color: "#6b6b6b" }}>
                            Total de contactos:{" "}
                            <strong style={{ color: "#27315D" }}>{total.toLocaleString()}</strong>
                        </Typography>
                    </div>
                )}
            </div>

            {/* Modal de importación */}
            <ImportContactsModal
                open={importOpen}
                onClose={() => setImportOpen(false)}
                onImport={handleImport}
            />

            {/* Modal (pantalla completa) para crear/editar contacto */}
            <ContactEditSection
                open={view === "edit"}
                mode={editState.mode}
                contact={editState.contact}
                onBack={backToList}
                onSave={handleSaveContact}
            />

            {/* Menú desplegable de filtro por columna (Género, Entidad,
                Localidad, Registro). Se abre al hacer clic en el encabezado. */}
            <Menu
                anchorEl={filterMenu.anchorEl}
                open={Boolean(filterMenu.anchorEl)}
                onClose={closeFilterMenu}
                getContentAnchorEl={null}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
            >
                <MenuItem
                    selected={!columnFilters[filterMenu.field]}
                    onClick={() => selectColumnFilter(filterMenu.field, "")}
                >
                    <em>Todos</em>
                </MenuItem>
                {(filterOptionsByField[filterMenu.field] || []).map((option) => (
                    <MenuItem
                        key={option}
                        selected={columnFilters[filterMenu.field] === option}
                        onClick={() => selectColumnFilter(filterMenu.field, option)}
                    >
                        {option}
                    </MenuItem>
                ))}
            </Menu>
        </div>
    );
};

export default ContactsSection;
